import { useScheme } from '@/lib/theme';
import { createRootRoute, Outlet, useRouterState, Link } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import { useEffect, useLayoutEffect, useState } from 'react';
import { initTelegram, haptic, getWebApp } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { House, Shield, Wallet, User, Megaphone, Check } from 'lucide-react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persister } from '@/lib/query-client';
import { APP_VERSION } from '@/lib/version';
import { useKeyboardOpen } from '@/lib/use-keyboard-open';
import { initAnalytics, track } from '@/lib/analytics';
import { ApiError, BotDisabledError, TelegramUnavailableError, currentBotId } from '@/lib/api';
import { BotDisabledScreen } from '@/components/BotDisabledScreen';
import { WebappDisabledScreen } from '@/components/WebappDisabledScreen';
import { TelegramUnavailableScreen } from '@/components/TelegramUnavailableScreen';
import { ErrorReportSheet } from '@/components/ErrorReportSheet';
import { StateScreen } from '@/components/StateScreen';
import { Btn } from '@/components/ui/tiles';
import { autoLog } from '@/lib/error-report';
import { SeasonBackdrop } from '@/components/season/SeasonBackdrop';
import { usePreviewRevision } from '@/lib/preview';

export const Route = createRootRoute({
  component: RootComponent,
});

/** Верх стопки тостов: под вырезом экрана и под шапкой Telegram, как у страницы. */
const TOAST_TOP =
  'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)';

// Анти-спам: один 5xx-toast не чаще раза в 5с (refetch'и могут лупить ошибку).
// Тост висит бесконечно (duration: Infinity) с кнопкой «Сообщить» — у юзера есть
// время нажать и отправить отчёт. openSheet прокидывается из компонента (доступ к state).
let lastServerErrorToast = 0;
function notifyServerError(error: unknown, openSheet: (e: unknown) => void) {
  const now = Date.now();
  if (now - lastServerErrorToast < 5000) return;
  lastServerErrorToast = now;
  toast.error(i18n.t('Backend down'), {
    description: i18n.t('Backend down desc'),
    duration: Infinity,
    action: {
      label: i18n.t('Report it'),
      onClick: () => openSheet(error),
    },
  });
}

function RootComponent() {
  const { t } = useTranslation();
  const keyboardOpen = useKeyboardOpen();
  const scheme = useScheme();
  // Bot отключён партнёром / удалён / JWT протух (401/403). Подписка на queryCache
  // ловит BotDisabledError даже из background-refetch'а — без этого экран показывал
  // только React ErrorBoundary, а до него ошибка из useQuery не доходила.
  const [botDisabled, setBotDisabled] = useState(false);
  const [webappOff, setWebappOff] = useState(false);
  // Telegram не отдал данные для входа (SDK не загрузился / пустая initData).
  const [tgError, setTgError] = useState<TelegramUnavailableError | null>(null);
  const [subGate, setSubGate] = useState<{ url?: string; name?: string }[] | null>(null);
  // Ошибка, по которой открыта форма отчёта (из persistent-тоста). null = закрыта.
  const [reportSheetError, setReportSheetError] = useState<unknown>(null);
  // Превью в кабинете: сменилось название или логотип, экран перемонтируется.
  const previewRevision = usePreviewRevision();

  useEffect(() => {
    initTelegram();
    initAnalytics();
    track('app_opened', { platform: getWebApp()?.platform });

    const handleError = (error: unknown) => {
      // Данные от Telegram не пришли — запрос до бэка не дошёл, в серверных логах такой
      // случай не виден вообще. Шлём маячок (эндпоинт принимает и без токена), иначе мы
      // о таких отказах не узнаём никак.
      if (error instanceof TelegramUnavailableError) {
        setTgError(error);
        void autoLog(error, { severity: 'error' });
        return;
      }
      if (error instanceof BotDisabledError) {
        const err = (error.body as { error?: { code?: string; context?: { channels?: { url?: string; name?: string }[] } } } | null)?.error;
        if (err?.code === 'app.subscription_required') {
          setSubGate(err.context?.channels ?? []);
          return;
        }
        // Бот жив, партнёр выключил только мини-апп: свой экран, а не «бот недоступен».
        if (err?.code === 'app.webapp_disabled') {
          setWebappOff(true);
          return;
        }
        setBotDisabled(true);
        return;
      }
      // 5xx / сетевой сбой (status 0) — бэк лёг. Тихо логируем на бэк (авто-лог с
      // дедупом) + показываем persistent-toast с кнопкой «Сообщить».
      if (error instanceof ApiError && (error.status >= 500 || error.status === 0)) {
        void autoLog(error, { severity: 'error' });
        notifyServerError(error, setReportSheetError);
      }
    };

    // Просканировать уже осевшие в кэше ошибки (prefetch'и из main.tsx стартуют
    // ДО mount'а root → их ошибка может прийти раньше подписки).
    for (const q of queryClient.getQueryCache().getAll()) {
      if (q.state.status === 'error') handleError(q.state.error);
    }

    const unsubQ = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handleError(event.action.error);
      }
    });
    const unsubM = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        handleError(event.action.error);
      }
    });
    return () => {
      unsubQ();
      unsubM();
    };
  }, []);

  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  // Вкладка загорается сразу по нажатию, а не когда отрисуется новый раздел.
  const [tappedTab, setTappedTab] = useState<string | null>(null);
  useLayoutEffect(() => setTappedTab(null), [currentPath]);
  // Экран для аналитики владельца: id роута (`/checkout/$planId`), а не адрес, иначе
  // каждый тариф стал бы отдельным экраном.
  const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId });
  useEffect(() => {
    if (routeId) track('screen_view', { route: routeId });
  }, [routeId]);

  if (subGate) {
    return (
      <SubscriptionGateScreen
        channels={subGate}
        onRetry={() => {
          queryClient.clear();
          setSubGate(null);
        }}
      />
    );
  }

  if (tgError) {
    return <TelegramUnavailableScreen reason={tgError.reason} />;
  }

  if (webappOff) {
    return <WebappDisabledScreen />;
  }

  if (botDisabled) {
    return <BotDisabledScreen />;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      // В buster входит bot_id: адрес мини-аппа общий у всех ботов, и кэш
      // тарифов или профиля одного бота успевал отрисоваться в другом до первого
      // рефетча. При несовпадении persist сам выбрасывает сохранённый кэш.
      persistOptions={{
        persister: persister!,
        maxAge: 24 * 60 * 60 * 1000,
        buster: `${APP_VERSION}:${currentBotId() ?? 'none'}`,
      }}
    >
      <div
        className="min-h-screen bg-background text-foreground"
        style={{
          // Вверху страницы два разных отступа, и складывать их напрямую нельзя.
          // `env(safe-area-inset-top)` это вырез экрана, но в вебвью Telegram он
          // на части устройств приходит нулём — там то же значение отдаётся
          // переменной `--tg-safe-area-inset-top`. Это ОДНО И ТО ЖЕ, поэтому
          // берём max, а не сумму: сумма даёт двойной отступ на айфоне.
          // `--tg-content-safe-area-inset-top` — своя шапка Telegram («✕ Закрыть»,
          // ⌄, ⋮), она рисуется ПОВЕРХ контента в fullscreen, и вот её нужно
          // прибавлять. Без неё верх страницы уезжает под шапку (заявка #56).
          paddingTop:
            'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)',
          // Внизу запас под меню: полоса высотой 58px поверх системного отступа
          // (жестовая навигация), плюс воздух. Числом без `env()` меню накрывало
          // последний ряд контента (заявка #69).
          // В «Облаке» меню висит таблеткой выше края, запас там больше (--nav-space).
          paddingBottom: 'calc(env(safe-area-inset-bottom) + var(--nav-space, 84px))',
        }}
      >
        <SeasonBackdrop />
        <div key={previewRevision} className="tl-page w-full max-w-md mx-auto">
          <Outlet />
        </div>

        {/* Четыре вкладки полосой во всю ширину (раскладка «Карта», 19.10.2026). Плавающая
            таблетка висела поверх контента и закрывала последние плитки. Тарифы
            открываются с главной и из подписки, отзывы и подключения живут в профиле. */}
        <nav
          aria-hidden={keyboardOpen}
          className={`tl-nav fixed inset-x-0 bottom-0 z-50 border-t border-[var(--t-line)] [backdrop-filter:blur(20px)_saturate(160%)] [-webkit-backdrop-filter:blur(20px)_saturate(160%)] transform-gpu transition-opacity duration-150 ${
            keyboardOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{ background: 'var(--calm-nav-bg)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="tl-nav-inner mx-auto grid h-[58px] max-w-md grid-cols-4">
          <TabLink to="/me" match={['/', '/me']} icon={<House size={21} />} label={t('Home')} currentPath={currentPath} tapped={tappedTab} onTap={setTappedTab} />
          <TabLink
            to="/subscription"
            match={['/subscription', '/devices', '/countries', '/renew', '/upgrade', '/plans', '/checkout', '/success', '/locations']}
            icon={<Shield size={21} />}
            label={t('Subscription tab')}
            currentPath={currentPath}
            tapped={tappedTab}
            onTap={setTappedTab}
          />
          <TabLink
            to="/wallet"
            match={['/wallet', '/topup', '/gift', '/payments', '/referral-withdraw']}
            icon={<Wallet size={21} />}
            label={t('Wallet')}
            currentPath={currentPath}
            tapped={tappedTab}
            onTap={setTappedTab}
          />
          <TabLink
            to="/profile"
            match={['/profile', '/settings', '/faq', '/status', '/reviews', '/activity', '/giveaways']}
            icon={<User size={21} />}
            label={t('Profile')}
            currentPath={currentPath}
            tapped={tappedTab}
            onTap={setTappedTab}
          />
          </div>
        </nav>

        {/* Тосты сверху. Снизу они ложились на кнопки шторок («Заморозить»,
            «Отмена»), а вечный тост об ошибке сервера закрывал их, пока его не смахнут.
            Отступ снизу к тому же не работал на телефоне: sonner ниже 600px берёт
            `mobileOffset`, а не `offset`. Сверху отступ тот же, что у страницы, чтобы
            не залезть под шапку Telegram в полноэкранном режиме. */}
        <Toaster
          position="top-center"
          /* Тосты по схеме мини-аппа, а не по ОС: схема идёт от темы Telegram. */
          theme={scheme}
          richColors
          offset={{ top: TOAST_TOP }}
          mobileOffset={{ top: TOAST_TOP }}
        />

        <ErrorReportSheet
          error={reportSheetError}
          open={reportSheetError !== null}
          onClose={() => setReportSheetError(null)}
        />
      </div>
    </PersistQueryClientProvider>
  );
}

/** Системная настройка «уменьшить движение» — плавный скролл ей противопоказан. */
function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function TabLink({
  to,
  match,
  icon,
  label,
  currentPath,
  tapped,
  onTap,
}: {
  to: string;
  match: string[];
  icon: React.ReactNode;
  label: string;
  currentPath: string;
  /** Вкладка, на которую только что нажали: горит, пока грузится её раздел. */
  tapped: string | null;
  onTap: (to: string) => void;
}) {
  const path = tapped ?? currentPath;
  const isActive = match.some((m) => (m === '/' ? path === '/' : path === m || path.startsWith(`${m}/`)));
  return (
    <Link
      to={to}
      preload="intent"
      // Без viewTransition: на время перехода браузер показывал застывший снимок
      // страницы вместе с меню, и подсветка вкладки опаздывала.
      onPointerDown={() => {
        if (!isActive) onTap(to);
      }}
      aria-current={isActive ? 'page' : undefined}
      onClick={(e) => {
        haptic('light');
        // Повторный тап по своей вкладке: наверх и свежие данные.
        if (currentPath === to || (to === '/me' && currentPath === '/')) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          void queryClient.refetchQueries({ type: 'active' });
        }
      }}
      className={`tl-tab relative flex h-full flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors duration-150 [&_svg]:size-6 [&_svg]:[stroke-width:1.7] ${
        isActive ? 'text-foreground' : 'text-[var(--t-faint)]'
      }`}
    >
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </Link>
  );
}

function SubscriptionGateScreen({
  channels,
  onRetry,
}: {
  channels: { url?: string; name?: string }[];
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <StateScreen
      icon={<Megaphone />}
      title={t('Subscribe to continue')}
      description={channels.length === 1 ? t('Subscribe channels one') : t('Subscribe channels many')}
      className="min-h-screen"
    >
      {channels.map((c, i) =>
        c.url ? (
          <a
            key={i}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => haptic('light')}
            className="calm-cta flex h-[52px] w-full items-center justify-center gap-2 rounded-[var(--r-btn)] text-[16px] font-semibold transition-transform active:scale-[0.97]"
          >
            <Megaphone size={18} /> {c.name || t('Channel')}
          </a>
        ) : null,
      )}
      <Btn variant="ghost" className="w-full" icon={<Check />} onClick={onRetry}>
        {t('I subscribed')}
      </Btn>
    </StateScreen>
  );
}

